import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function Insights() {
  const [posts, setPosts] = useState(null);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  const cats = useMemo(() => {
    if (!posts) return ["All"];
    return ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  }, [posts]);

  const filtered = cat === "All" ? posts : posts?.filter((p) => p.category === cat);

  return (
    <>
      <SEO
        title="Insights — Vector Growth and Advisory"
        description="Advisory insights on digital acquisition funnels, AI-driven Salesforce CRM automation and commercial market assessments."
      />
      <section className="relative overflow-hidden pt-40 pb-14 md:pt-48" data-testid="insights-hero">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
          <Eyebrow>Perspectives & advisory insights</Eyebrow>
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl" data-testid="insights-headline">
            Thinking that <span className="text-gradient">moves pipelines.</span>
          </h1>
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="insights-filter">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                  cat === c
                    ? "border-neon bg-neon/10 text-neon"
                    : "hairline text-slate-500 hover:border-neon/50 hover:text-slate-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-28 md:px-10" data-testid="insights-list">
        {filtered === null && (
          <div className="grid gap-5 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[380px] animate-pulse rounded-2xl border hairline bg-surface/60" data-testid="insight-skeleton" />
            ))}
          </div>
        )}
        {filtered && (
          <div className="grid gap-5 md:grid-cols-3" data-testid="insights-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`insight-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border hairline bg-surface/70 transition-all duration-500 hover:border-neon/40"
                >
                  <div className="h-48 overflow-hidden">
                    <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-neon/80">{p.category}</p>
                    <h2 className="mt-3 font-display text-lg font-bold leading-snug text-ink">{p.title}</h2>
                    <p className="mt-3 flex-1 text-[13px] leading-relaxed text-slate-500">{p.excerpt}</p>
                    <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      {p.date} · {p.read_time}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
        {filtered && filtered.length === 0 && (
          <p className="py-16 text-center font-mono text-sm text-slate-500" data-testid="insights-empty">
            No insights in this category yet.
          </p>
        )}
      </section>
    </>
  );
}
